/**
 * Nominal-size column index for tolerance tables (ISO style):
 * bands are (previousUpper, upper] with the upper limit inclusive, so a value exactly
 * on a step boundary uses the column for the smaller nominal band.
 */

export type ToleranceColumnExceededMax =
    /** Last table column is also the maximum nominal column (e.g. ISO 286-2 up to 500 mm). */
    | 'clamp-to-last-edge-column'
    /** One more column exists beyond the last finite upper edge (e.g. ISO 2768-1 angular "over 400 mm"). */
    | 'append-open-ended-column';

/**
 * ISO 286-2 basic size steps (mm): column i ends at inclusiveUpperEdgesMm[i].
 * Steps match the metric H/h chart at https://www.machiningdoctor.com/calculators/tolerances/tol/
 * (first band 0–3 mm; includes 120–160 / 160–180 / 180–225 / 225–250 splits). Nominal above 3150 mm
 * uses the last column.
 */
export const ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM = [
    3, 6, 10, 18, 30, 50, 80, 120, 160, 180, 225, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000,
    2500, 3150
] as const;

/** ISO 2768-1 linear nominal ranges (mm): through "over 2000 up to and including 4000". */
export const ISO2768_LINEAR_INCLUSIVE_UPPER_MM = [3, 6, 30, 120, 400, 1000, 2000, 4000] as const;

/**
 * ISO 2768-1 Table 2 — external radii and chamfer heights (mm): "up to 3", "over 3 up to 6", "over 6".
 */
export const ISO2768_RADIUS_CHAMFER_INCLUSIVE_UPPER_MM = [3, 6] as const;

/** ISO 2768-1 angular: shorter side length (mm). */
export const ISO2768_ANGULAR_SHORTER_SIDE_INCLUSIVE_UPPER_MM = [10, 50, 120, 400] as const;

/**
 * @param nominalMm — nominal or reference length (mm)
 * @param inclusiveUpperEdgesMm — strictly increasing inclusive upper limits for successive columns
 * @param exceededMax — how to index when nominal is greater than all edges
 */
export function toleranceTableColumnIndex(
    nominalMm: number,
    inclusiveUpperEdgesMm: readonly number[],
    exceededMax: ToleranceColumnExceededMax
): number {
    const d = nominalMm;
    if (!isFinite(d) || d < 0) {
        return 0;
    }
    for (let i = 0; i < inclusiveUpperEdgesMm.length; i++) {
        if (d <= inclusiveUpperEdgesMm[i]) {
            return i;
        }
    }
    if (exceededMax === 'clamp-to-last-edge-column') {
        return Math.max(0, inclusiveUpperEdgesMm.length - 1);
    }
    return inclusiveUpperEdgesMm.length;
}
